/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import * as _ from 'underscore';
import { Logger } from '@core/lib/services';
import { Injectable } from '@angular/core';
import { SearchCriteriaService } from '@app/ui/search/search-criteria.service';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { appConstants } from '@app/common/app-constants';
import { WorkItemService } from '@app/data-model/item/work-item.service';
import { AppConfigService } from '@app/common/app-config.service';
import { SearchList } from '@app/data-model/list/search-list';
import { Count } from '@core/lib/components/to-do-count/count';
import { MessageService } from '@core/lib/services/message.service';
import { UtilService } from '@app/util/util.service';
import { DataTransformationService } from '@app/common/data-transformation.service';
import { isEmpty, isUndefined } from '@core/lib/helpers/helpers';
import { SearchCriteriaType, SearchParams } from '@app/common/types/search-params';
import { DatepickerRangeOptions } from '@wfm/core';
import { LayoutService } from '@app/ui/common/layout.service';

const log = new Logger('SearchService');

@Injectable({
  providedIn: 'root'
})
export class SearchService {
  private prefixRegex = /[%|*]$/;
  private suffixRegex = /^[%|*]/;

  private MatchPrefix = 'prefix:';
  private MatchExact = 'exact:';
  private MatchSuffix = 'suffix:';

  constructor(
    private appConfigService: AppConfigService,
    private searchCriteriaService: SearchCriteriaService,
    private dataService: ProxyDataService,
    private workItemService: WorkItemService,
    private messageService: MessageService,
    private utilService: UtilService,
    private dataTransformationService: DataTransformationService,
    private layoutService: LayoutService
  ) {}

  populateNextPage(list: SearchList) {
    return new Promise((resolve, reject) => {
      this.getSearchExamsList(1, parseInt(this.appConfigService.maxTotalCount, 10) + 1, list.searchCriteriaInfo).then(
        (data: any[]) => {
          log.debug('search-exam-list -- Search call executed, creating promise handler');
          log.debug('search-exam-list -- Search call data received');
          list.count = new Count(data && data.length, 0, 0);
          resolve(this.workItemService.createWorkItems('search', data, undefined));
        },
        error => {
          log.error(`Error occurred while doing quick search, INPUT: ${JSON.stringify(list.searchCriteriaInfo)},
            ERROR: ${error}`);
          reject(error);
        }
      );
    });
  }

  specialCharactersExist(searchCriteriaInfo) {
    return this.utilService.specialCharactersExist(searchCriteriaInfo);
  }

  joinParams(data) {
    const params = [];
    let val;
    for (const prop in data) {
      if ({}.hasOwnProperty.call(data, prop)) {
        if (Array.isArray(data[prop]) && data[prop].length > 0) {
          val = data[prop].join(',');
        } else if (typeof data[prop] === 'string' || typeof data[prop] === 'number') {
          val = data[prop];
        }

        if (val || val === 0) {
          params.push([prop, val].join(':'));
        }
      }
    }
    return params.join(';');
  }

  parseFilter(filter) {
    const wrappedFilter = _({});
    return this.joinParams(wrappedFilter.extend[filter instanceof Array ? 'apply' : 'call'](wrappedFilter, filter));
  }

  extractedGetWorkItemParams(parameters, params) {
    // Filter params
    if (parameters.filter) {
      params.filter = this.parseFilter(parameters.filter);
    }

    // Sort params
    if (!!parameters.sort && !_.isEmpty(parameters.sort.item)) {
      if (parameters.sort?.item.priority) {
        delete params.sort?.priority;
      }
      if (parameters.sort?.item?.timeOfExam || parameters.sort?.item?.verifiedTimeOfExam) {
        delete params.sort?.verifiedTimeOfExam;
        delete params.sort?.timeOfExam;
      }
    } else {
      parameters.sort = { item: {} };
    }

    const sortParams = this.joinParams(Object.assign(parameters.sort.item, params.sort));
    if (!isEmpty(sortParams)) {
      params.sort = sortParams;
    }
  }

  getSearchExamsCount(searchCriteriaInfo) {
    const params = this.buildServiceParams(searchCriteriaInfo);
    if (searchCriteriaInfo.filterParams) {
      this.extractedGetWorkItemParams(searchCriteriaInfo.filterParams, params);
    }

    return this.dataService.get(appConstants.appURLs.searchItemCount, params);
  }

  getSearchExamsList(startR, endR, searchParams: SearchParams) {
    const params: any = searchParams.buildRequestParams(
      searchParams.searchCriteriaType,
      this.layoutService.getActiveWLColumnsAttrId()
    );
    if (searchParams['filterParams']) {
      this.extractedGetWorkItemParams(searchParams['filterParams'], params);
    }
    params['startR'] = startR;
    params['endR'] = endR;
    if (isUndefined(params.advancedSearch)) {
      params['advancedSearch'] = searchParams && searchParams.searchCriteriaType === SearchCriteriaType.ADVANCED_SEARCH;
    }
    return this.dataService.get(appConstants.appURLs.searchExams, params);
  }

  getPatients(searchCriteriaInfo, maxPatientCount) {
    const params = this.buildServiceParams(searchCriteriaInfo);
    if (maxPatientCount !== undefined) {
      params[`startR`] = 1;
      params[`endR`] = maxPatientCount;
    }

    return this.dataService.get(appConstants.appURLs.searchPatients, params);
  }

  getSearchlists() {
    return [
      {
        id: 'SearchExams',
        name: 'search',
        desc: 'Search MRN/Accession',
        default: false,
        url: 'search/requested-procedures'
      }
    ];
  }

  buildServiceParamsWithWildcard(searchCriteria) {
    const isPrefix = this.prefixRegex.test(searchCriteria);
    const isSuffix = this.suffixRegex.test(searchCriteria);
    if (isPrefix) {
      const lastIndex = searchCriteria.length - 1;
      return this.MatchPrefix + searchCriteria.trim().slice(0, lastIndex);
    }
    if (isSuffix) {
      return this.MatchSuffix + searchCriteria.trim().substr(1);
    }
    return this.MatchExact + searchCriteria;
  }

  buildServiceParams(searchCriteriaInfo, filter?) {
    const params = {} as any;
    if (searchCriteriaInfo && searchCriteriaInfo.searchCriteria && searchCriteriaInfo.patientId) {
      params.patientId = searchCriteriaInfo.patientId;
    }
    if (searchCriteriaInfo && searchCriteriaInfo.searchCriteria) {
      if (searchCriteriaInfo.searchCriteriaType === 'MRN / Patient ID') {
        if (this.appConfigService.isNHIEnabled()) {
          searchCriteriaInfo.searchCriteria = this.dataTransformationService.getNonFormattedNHI(
            searchCriteriaInfo.searchCriteria
          );
        }
        params.mrn = encodeURIComponent(this.buildServiceParamsWithWildcard(searchCriteriaInfo.searchCriteria));
      } else if (searchCriteriaInfo.searchCriteriaType === 'Accession Number') {
        params.accession = encodeURIComponent(this.buildServiceParamsWithWildcard(searchCriteriaInfo.searchCriteria));
      } else if (searchCriteriaInfo.searchCriteriaType === 'Patient Name') {
        params.patientName = encodeURIComponent(searchCriteriaInfo.searchCriteria);
      } else if (searchCriteriaInfo.searchCriteriaType === 'Advanced') {
        return this.buildAdvancedServiceParams(searchCriteriaInfo.searchCriteria, filter);
      }
    }
    // Date Token
    if (searchCriteriaInfo && searchCriteriaInfo.dateToken && searchCriteriaInfo.dateToken !== 'All') {
      params['dateToken'] = searchCriteriaInfo.dateToken;
    }
    return params;
  }

  buildAdvancedServiceParams(searchCriteria, filter) {
    let mapping;
    if (filter) {
      mapping = appConstants.ADVANCED_SEARCH_FILTER_KEYS;
    } else {
      mapping = appConstants.ADVANCED_SEARCH_KEYS;
    }
    const urlEncodedCharacters = '%2B';

    const params = {};
    Object.getOwnPropertyNames(searchCriteria).forEach(criteriaValue => {
      this.extractedOwnPropertyName(mapping, criteriaValue, searchCriteria, params, urlEncodedCharacters);
    });

    this.setPreferenceParams(params, searchCriteria);
    return params;
  }

  extractedOwnProperty(value, params, criteria) {
    if (value.key === 'accession' || value.key === 'mrn') {
      params[value.key] = encodeURIComponent(this.buildServiceParamsWithWildcard(criteria[value.field]));
    } else if (value.key === 'patientName') {
      params[value.key] = encodeURIComponent(criteria[value.field]);
    } else if (!!params[value.key]) {
      // handling search using department name and department C-Key at the same time
      params[value.key].push(criteria[value.field]);
    } else {
      params[value.key] = criteria[value.field];
    }
  }

  extractedOwnPropertyNa(criteria, params, value) {
    if (Array.isArray(criteria)) {
      params[value.key] = _.pluck(criteria, value.field);
    } else if (criteria[value.field]) {
      this.extractedOwnProperty(value, params, criteria);
    }

    if (
      value.key === 'currentLocation' ||
      value.key === 'specialties' ||
      value.key === 'reportStatusIds' ||
      value.key === 'initialInterpreters'
    ) {
      params.advancedSearch = true;
    }
    if (criteria.key === appConstants.EXCLUDE_ZERO_IMAGE.key && criteria.state) {
      params[appConstants.EXCLUDE_ZERO_IMAGE.key] = appConstants.EXCLUDE_ZERO_IMAGE.value;
    }
  }

  extractedOwnPropertyNam(value, criteria, params) {
    if (value && typeof value === 'object') {
      this.extractedOwnPropertyNa(criteria, params, value);
    }
  }

  extractedOwnPropertyName(mapping, criteriaValue, searchCriteria, params, urlEncodedCharacters) {
    const value = mapping[criteriaValue];
    const criteria = searchCriteria[criteriaValue];
    this.extractedOwnPropertyNam(value, criteria, params);

    this.handlDatePickerGap(value, searchCriteria, criteriaValue, params, urlEncodedCharacters);
  }

  // Handling the gap between Search API and Date Picker's Today date token
  handlDatePickerGap(value, searchCriteria, criteriaValue, params, urlEncodedCharacters) {
    if (value && typeof value === 'string') {
      if (value === 'dateToken' || value === 'verifiedDateRangeToken') {
        if (searchCriteria[criteriaValue] === 'T+0') {
          params[value] = 'T-0';
        } else if (searchCriteria[criteriaValue].indexOf('+') !== -1) {
          params[value] = searchCriteria[criteriaValue].replace('+', urlEncodedCharacters);
        } else {
          params[value] = searchCriteria[criteriaValue];
        }
      } else {
        params[value] = searchCriteria[criteriaValue];
      }
    }
  }

  // set preference to params based on user selection.
  private setPreferenceParams(params, searchCriteria) {
    const selectedPreference = {};
    // Stat on top preference
    if (this.searchCriteriaService.getUserPreference('statsOnTop')) {
      selectedPreference[appConstants.SORT_BY.Priority] = appConstants.SORT_BY.descendingOrder;
    }

    // Sort by orderBy preference
    const selectedOrderBy = searchCriteria[appConstants.ORDER_BY.mapsTo].stateName;
    selectedPreference[appConstants.SORT_BY[selectedOrderBy]] = appConstants.SORT_BY.descendingOrder;

    params[appConstants.SORT_BY.key] = selectedPreference;
  }

  isSearchList(list) {
    return !!list && !!list.searchCriteriaInfo;
  }

  isAdvancedSearchList(list) {
    return this.isSearchList(list) && list.searchCriteriaInfo.searchCriteriaType === SearchCriteriaType.ADVANCED_SEARCH;
  }

  getWorkItemByExamIdentifier(searchParams: SearchParams, examIdentifier: string) {
    return new Promise((resolve, reject) => {
      this.getSearchExamsList(1, 50, searchParams).then(
        (result: any) => {
          if (result && result.length > 0) {
            if (result.length > 1) {
              log.error('SearchService - duplicate exam identifiers were found for ' + examIdentifier);
              reject(appConstants.FOUND_MORE);
            } else {
              const workItem = this.workItemService.getWorkItem(result[0]);
              if (workItem && !_.isUndefined(workItem.id)) {
                this.dataService.getWorkItemStates(workItem.id, this.appConfigService.getUserId()).then(
                  states => {
                    if (states && Array.isArray(states)) {
                      // workItem['protocolWorklist'] = false;
                      workItem.state = states[0].newState;
                      resolve(workItem);
                    }
                  },
                  error => {
                    log.error(
                      'SearchService - Error occurred while getting ' +
                        'work item state, exam identifier: ' +
                        examIdentifier +
                        ', Error - ' +
                        JSON.stringify(error)
                    );
                    reject(appConstants.WORKITEM_STATE);
                  }
                );
              } else {
                log.error('SearchService - Unable to create a work item for: ' + examIdentifier);
                reject(appConstants.WORKITEM_CREATE);
              }
            }
          } else {
            log.error('SearchService - Unable to find a work item for: ' + examIdentifier);
            reject(appConstants.FOUND_NONE);
          }
        },
        error => {
          log.error('SearchService - Error occurred while searching for ' + examIdentifier + ', Error: ' + error);
          reject(appConstants.SEARCH_ERROR);
        }
      );
    });
  }

  getWorkItemBySIUID(SIUID) {
    const searchParams = new SearchParams(SearchCriteriaType.STUDY_INSTANCE_UID);
    searchParams.studyInstanceUID = SIUID;
    searchParams.timeOfExam = {
      option: DatepickerRangeOptions.allDates,
      startTime: undefined,
      endTime: undefined
    };

    return this.getWorkItemByExamIdentifier(searchParams, SIUID);
  }

  getWorkItemByAccession(accNum) {
    const searchParams = new SearchParams(SearchCriteriaType.ACCESSION);
    searchParams.accession = accNum;
    searchParams.timeOfExam = {
      option: DatepickerRangeOptions.allDates,
      startTime: undefined,
      endTime: undefined
    };

    return this.getWorkItemByExamIdentifier(searchParams, accNum);
  }
}
